import { useEffect, useState } from "react";
import {
  AlertCircle,
  Building,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  PenIcon,
  Search,
} from "lucide-react";
import { Button } from "@components/common/button";
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card";
import { Input } from "@components/common/input";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  PRICE_LIST_EDIT_ENDPOINT,
  FINANCIAL_SERVER_URL,
  PRICE_LIST_BY_ID_ENDPOINT,
} from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import axios from "axios";
import { useAuth } from "@pma/stores/auth-store";
import { Link } from "react-router-dom";
import { DictType } from "@pma/types";
import useTranslator from "@pma/hooks/use-translator";
import { format } from "date-fns";
import { he } from "date-fns/locale";
import { toast } from "@pma/hooks/use-toast";
import PmsLoader from "@components/common/loader";
import { FINANCIAL_PRICE_LIST_PAGE_ROUTE } from "@pma/utils/page-routes";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@pma/components/common/table";
import { cn } from "@pma/utils/helpers";
import { Badge } from "@components/common/badge";
import { Link1Icon } from "@radix-ui/react-icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip";

interface Props {
  priceList: DictType;
  refetch: CallableFunction;
  idx: number;
}
const PriceListCard = ({ priceList, refetch, idx }: Props) => {
  const [newName, setNewName] = useState(priceList?.name);
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");

  const orgId = authConfig?.last_organization_id;

  const isHebrew = locale === "he";
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const getCompanyPriceList = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_BY_ID_ENDPOINT + "",
      {},
      {
        params: {
          price_id: priceList?.id,
          organization_id: orgId,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const { data: priceLists, isLoading } = useQuery({
    queryKey: ["company-price-list", priceList?.id, newName],
    queryFn: getCompanyPriceList,
    enabled: expanded,
  });

  const priceListItems = priceLists?.price_items?.items;
  const filteredItems =
    priceListItems?.filter(
      (item: DictType) =>
        item?.number?.includes(searchQuery) ||
        item?.units?.includes(searchQuery) ||
        String(item?.price)?.includes(searchQuery) ||
        item?.description?.toLowerCase().includes(searchQuery.toLowerCase()),
    ) || [];

  const editPriceList = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + PRICE_LIST_EDIT_ENDPOINT,
      {
        name: newName,
        price_id: priceList?.id,
        organization_id: orgId,
      },
      {
        params: {
          organization_id: orgId,
        },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const editPriceListMutation = useMutation({
    mutationKey: ["edit-price-list"],
    mutationFn: editPriceList,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Price list edited successfully"),
        variant: "success",
      });
      setIsEditing(false);

      refetch();
    },
  });

  const getStatusBadge = (item: DictType) => {
    if (item?.deleted) {
      return (
        <Badge
          variant='destructive'
          className='flex items-center justify-center gap-1 w-fit min-w-[100px]'
        >
          <span className='text-xs'>{translate("Deleted")}</span>
        </Badge>
      );
    } else if (item?.is_item) {
      return (
        <Badge
          variant='default'
          className='flex items-center justify-center gap-1 w-fit min-w-[100px]'
        >
          <CheckCircle2 className='h-3 w-3' /> {translate("Item")}
        </Badge>
      );
    } else {
      return (
        <Badge
          variant='disabled'
          className='flex items-center justify-center gap-1 w-fit min-w-[100px]'
        >
          <AlertCircle className='h-3 w-3' /> {translate("Not an Item")}
        </Badge>
      );
    }
  };

  useEffect(() => {
    if (idx === 0) {
      setExpanded(true);
    }
  }, []);
  return (
    <Card key={priceList?.id}>
      <CardHeader className='flex flex-row justify-between'>
        {isEditing ? (
          <div className='flex items-center gap-2'>
            <input
              className='rounded border font-normal flex-1 mb-1 bg-white/20 px-2 outline-none focus:border-primary'
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              onBlur={() => {
                if (newName === priceList?.name) {
                  setIsEditing(false);
                  return;
                }
                editPriceListMutation.mutate(priceList?.id);
              }}
              onKeyDown={(e) => {
                if (e.key !== "Enter") {
                  return;
                }
                editPriceListMutation.mutate(priceList?.id);
              }}
            />
            <Building size={18} />
          </div>
        ) : (
          <CardTitle className='flex flex-col gap-2 relative'>
            <Button
              size={"icon"}
              className={cn("absolute text-xs h-4 w-4  text-primary -top-4", "-right-3")}
              variant={"ghost"}
              onClick={() => setIsEditing(!isEditing)}
            >
              <PenIcon size={14} />
            </Button>
            <span>{priceList?.name}</span>
            <small className='text-[10px] gap-0.5 w-full p-0'>
              {translate("Added")}:
              {format(priceList?.created_at, "dd MMM yyy", {
                locale: isHebrew ? he : undefined,
              })}
            </small>
          </CardTitle>
        )}
        <div className='flex justify-between text-xs'>
          <div className='flex gap-3 w-full justify-start p-1'>
            <Button
              onClick={() => setExpanded(!expanded)}
              size={"icon"}
              className='text-xs h-7 w-7'
              variant={"outline"}
            >
              {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent>
          <div className='flex justify-between gap-2 mb-3'>
            <div className='relative'>
              <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder={translate("Search items") + "..."}
                className='pl-8 w-full md:w-[300px]'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Tooltip>
              <TooltipTrigger>
                <Link to={FINANCIAL_PRICE_LIST_PAGE_ROUTE + `/${priceList?.id}`}>
                  <Link1Icon />
                </Link>
              </TooltipTrigger>
              <TooltipContent>{translate("Go to price list")}</TooltipContent>
            </Tooltip>
          </div>
          {isLoading && <PmsLoader />}
          {!isLoading && filteredItems?.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className='font-semibold'>
                  <TableHead className={isHebrew ? "text-right" : "text-left"}>
                    {translate("Number")}
                  </TableHead>
                  <TableHead className={isHebrew ? "text-right" : "text-left"}>
                    {translate("Description")}
                  </TableHead>
                  <TableHead className={isHebrew ? "text-right" : "text-left"}>
                    {translate("Units")}
                  </TableHead>
                  <TableHead className={isHebrew ? "text-right" : "text-left"}>
                    {translate("Price")}
                  </TableHead>
                  <TableHead className={isHebrew ? "text-right" : "text-left"}>
                    {translate("Status")}
                  </TableHead>
                  <TableHead className={isHebrew ? "text-right" : "text-left"}>
                    {translate("Created")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems?.map((priceList: DictType) => (
                  <TableRow key={priceList?.id}>
                    <TableCell className='font-medium'>{priceList?.number}</TableCell>

                    <TableCell className='text-wrap md:max-w-[300px]'>
                      {priceList?.description || "---"}
                    </TableCell>
                    <TableCell>{priceList?.units || "---"}</TableCell>
                    <TableCell>{priceList?.price || "---"}</TableCell>
                    <TableCell>{getStatusBadge(priceList)}</TableCell>
                    <TableCell>
                      {format(priceList?.created_at, "dd MMM yyy", {
                        locale: isHebrew ? he : undefined,
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isLoading && filteredItems?.length < 1 && (
            <div className='flex justify-center items-center'>{translate("No items found")}</div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default PriceListCard;
